import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Loader2, ClipboardCheck, Plus, ArrowUpRight, Users, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status-pill metadata. Single source of truth for both list + detail views.
const STATUS_META = {
  draft:               { label: 'Draft',                 color: '#71717a' },
  pending_brand_review:{ label: 'Pending Brand Review',  color: '#FFB800' },
  finalized:           { label: 'Finalized',             color: '#0055FF' },
  outreach_started:    { label: 'Outreach Started',      color: '#00C972' },
};

// Campaign Manager-facing list of Review Lists.
export const CMReviewLists = () => {
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: '', campaign_id: '', notes: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: bulk }] = await Promise.all([
        axios.get(`${API_URL}/api/review-lists`, { withCredentials: true }),
        axios.get(`${API_URL}/api/bulk/campaigns`, { withCredentials: true }),
      ]);
      setLists(Array.isArray(data) ? data : []);
      setCampaigns(Array.isArray(bulk) ? bulk : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load review lists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.campaign_id) {
      toast.error('Name and campaign are required'); return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/review-lists`,
        { name: form.name.trim(), campaign_id: form.campaign_id, notes: form.notes.trim() || null },
        { withCredentials: true });
      toast.success('Review list created');
      window.location.href = `/cm/review-lists/${data.id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="cm-review-lists-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <ClipboardCheck className="text-[#FF451A]" size={32} />
                Review Lists
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Curate creator lists, send to brand for shortlist/reject, then start outreach.</p>
            </div>
            <Button onClick={() => setOpenCreate(true)} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-2" data-testid="create-review-list-button">
              <Plus size={16} /> New Review List
            </Button>
          </div>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : lists.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="cm-review-lists-empty">
              <ClipboardCheck className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No review lists yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Create one to send creators to a brand for selection.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cm-review-lists-grid">
              {lists.map((rl) => {
                const meta = STATUS_META[rl.status] || STATUS_META.draft;
                return (
                  <Link
                    key={rl.id}
                    to={`/cm/review-lists/${rl.id}`}
                    className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:-translate-y-[2px] transition-all flex flex-col gap-3"
                    data-testid="cm-review-list-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate">{rl.name}</div>
                        <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-0.5">{rl.campaign_name} · {rl.brand_name}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full shrink-0"
                        style={{ backgroundColor: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <Counter label="Total" value={rl.summary?.total ?? 0} color="#71717a" />
                      <Counter label="Shortlisted" value={rl.summary?.shortlisted ?? 0} color="#00C972" />
                      <Counter label="Rejected" value={rl.summary?.rejected ?? 0} color="#FF3B30" />
                    </div>
                    <div className="text-xs text-[#FF451A] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open list <ArrowUpRight size={11} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New review list</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              Pick a bulk campaign and give the list a name. Add creators in the next step.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Sanfe Acne — Review #1"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="review-list-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Campaign</label>
              <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
                className="w-full rounded-xl text-sm p-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="review-list-campaign-select">
                <option value="">Select a campaign…</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.campaign_name} ({c.brand_name})</option>
                ))}
              </select>
              {campaigns.length === 0 && (
                <div className="text-[11px] text-[#FFB800] mt-1">No bulk campaigns assigned to you yet — admin must route a brief first.</div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Notes (optional)</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} placeholder="Anything the brand should know about this batch"
                className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={creating} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1 disabled:opacity-40" data-testid="review-list-create-submit">
                {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />} Create
              </Button>
              <Button type="button" onClick={() => setOpenCreate(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Counter = ({ label, value, color }) => (
  <div className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-lg p-2">
    <div className="text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">{label}</div>
    <div className="text-base font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);
